import { useState } from 'react';
import { Users, Link as LinkIcon, Siren, MessageSquare, Pencil, Plus } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CharCounter } from '@/components/shared/CharCounter';
import { LinksEditor } from '@/components/onboarding/enrichment/editors/LinksEditor';
import {
  SlotFieldsList,
  makeEmptySlot,
  availableSlotTypes,
  stripDeadSlotKeys,
} from '@/components/onboarding/enrichment/editors/SlotsEditor';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS, OBJECTIVES } from '@/hooks/useKbProfileForm';
import type { KbProfileFormValues } from '@/hooks/useKbProfileForm';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import type { Objective, SlotDefinition } from '@/services/kbProfileService';
import { FieldHelp } from '@/components/onboarding/FieldHelp';

const OBJECTIVE_ICONS: Record<Objective, React.ElementType> = {
  capture_contacts: Users,
  send_to_link: LinkIcon,
  urgent_dispatch: Siren,
  answer_only: MessageSquare,
};

interface ObjectiveStepProps {
  form: UseFormReturn<KbProfileFormValues>;
}

export function ObjectiveStep({ form }: ObjectiveStepProps) {
  const { t } = useTranslation();
  const botGoal = form.watch('bot_goal');
  // F095 — once a family is chosen, the other cards collapse to title-only so
  // the obligatory handoff field below doesn't get buried (alpha A09). Before
  // any selection all cards stay expanded — the owner needs the context to
  // choose (in practice capture_contacts is pre-set on leaving step 1).
  const hasSelection = Boolean(botGoal);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium">{t('onboarding.kbForm.step5.objective_label')}</p>
          <FieldHelp baseKey="onboarding.kbForm.step5.bot_goal" />
        </div>
        <p className="text-xs text-muted-foreground">
          {t('onboarding.kbForm.step5.objective_hint')}
        </p>
      </div>

      <div
        className="space-y-2"
        role="radiogroup"
        aria-label={t('onboarding.kbForm.step5.objective_label')}
      >
        {OBJECTIVES.map((obj) => (
          <ObjectiveCard
            key={obj}
            objective={obj}
            selected={botGoal === obj}
            collapsed={hasSelection && botGoal !== obj}
            onSelect={() => form.setValue('bot_goal', obj, { shouldValidate: true })}
          />
        ))}
      </div>

      {form.formState.errors.bot_goal && (
        <p className="text-xs text-destructive" role="alert">
          {form.formState.errors.bot_goal.message}
        </p>
      )}

      <FormField
        control={form.control}
        name="objective_freetext"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">
              <span className="flex items-center gap-1">
                <span>{t('onboarding.kbForm.step5.freetext_label')}</span>
                <FieldHelp baseKey="onboarding.kbForm.step5.objective_freetext" />
              </span>
            </FormLabel>
            <p className="text-xs text-muted-foreground">
              {t('onboarding.kbForm.step5.freetext_hint')}
            </p>
            <FormControl>
              <Textarea
                rows={3}
                className="resize-none"
                placeholder={t('onboarding.kbForm.step5.freetext_placeholder')}
                maxLength={CAPS.objective_freetext}
                {...field}
              />
            </FormControl>
            <CharCounter value={field.value ?? ''} max={CAPS.objective_freetext} />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="handoff_contact"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">
              <span className="flex items-center gap-1">
                <span>{t('onboarding.kbForm.step5.handoff_label')}</span>
                <FieldHelp baseKey="onboarding.kbForm.step5.handoff_contact" />
              </span>
            </FormLabel>
            <p className="text-xs text-muted-foreground">
              {t('onboarding.kbForm.step5.handoff_hint')}
            </p>
            <FormControl>
              <Input
                className="h-11"
                placeholder={t('onboarding.kbForm.step5.handoff_placeholder')}
                maxLength={CAPS.handoff_contact}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

interface ObjectiveCardProps {
  objective: Objective;
  selected: boolean;
  /** F095 — a selection exists elsewhere: shrink to icon + title (1 line). */
  collapsed?: boolean;
  onSelect: () => void;
}

function ObjectiveCard({ objective, selected, collapsed = false, onSelect }: ObjectiveCardProps) {
  const { t } = useTranslation();
  const Icon = OBJECTIVE_ICONS[objective];

  return (
    <div
      data-testid={`objective-card-${objective}`}
      onClick={onSelect}
      className={[
        'flex flex-col min-h-11 rounded-xl border transition-colors duration-200',
        selected ? 'border-foreground bg-muted' : 'border-border hover:bg-muted/50 cursor-pointer',
      ].join(' ')}
    >
      <label
        className={[
          'flex gap-3 px-4 py-3 cursor-pointer',
          collapsed ? 'items-center' : 'items-start',
        ].join(' ')}
      >
        <input
          type="radio"
          value={objective}
          checked={selected}
          onChange={onSelect}
          className="sr-only"
          aria-label={t(`onboarding.kbForm.objective_${objective}`)}
        />
        <Icon
          className={['h-5 w-5 shrink-0', collapsed ? '' : 'mt-0.5'].join(' ')}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">
            {t(`onboarding.kbForm.objective_${objective}`)}
          </span>
          {/* Description + example hidden when collapsed — only the selected
              card (and the pre-selection state) shows the full context. */}
          {!collapsed && (
            <>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t(`onboarding.kbForm.objective_${objective}_description`)}
              </p>
              <p className="text-xs text-muted-foreground/80 mt-0.5">
                {t(`onboarding.kbForm.objective_${objective}_example`)}
              </p>
            </>
          )}
        </div>
      </label>

      {/* Conditional sub-configuration — only inside the selected (expanded)
          card, so the choice flows straight into what makes it effective. */}
      {selected && (
        <div className="px-3 pb-4 pt-1 border-t border-border/60 mb-3 -mt-1 sm:border-t-0 sm:pt-0">
          {objective === 'capture_contacts' && <QualificationSlotsBlock />}
          {objective === 'send_to_link' && <LinkSubConfigBlock />}
          {objective === 'urgent_dispatch' && (
            <p className="text-xs text-muted-foreground">
              {t('onboarding.kbForm.step5.urgent_hint')}
            </p>
          )}
          {objective === 'answer_only' && (
            <p className="text-xs text-muted-foreground">
              {t('onboarding.kbForm.step5.answer_only_hint')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Family 1 — optional qualification questions, the SlotsEditor field list
 * embedded inline (F077 FR2). Persists via the existing PATCH flow. */
function QualificationSlotsBlock() {
  const { t } = useTranslation();
  const { kbProfile, updateKbProfile } = useOnboardingState();
  const initial = kbProfile?.slot_definitions ?? [];
  // FR3: offer `session` here too when the schedule grid has ≥1 session — the
  // wizard block was missing this gate, so `session` never appeared in the
  // qualification step even with a grid configured.
  const availableTypes = availableSlotTypes((kbProfile?.operation?.sessions?.length ?? 0) > 0);
  const [slots, setSlots] = useState<SlotDefinition[]>(
    initial.length > 0 ? [...initial] : [makeEmptySlot()]
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setError(null);
    setFieldErrors(null);
    try {
      await updateKbProfile({
        slot_definitions: slots.filter((s) => s.label.trim()).map(stripDeadSlotKeys),
      });
      setSaved(true);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="objective-slots-block" className="space-y-3">
      <div className="flex items-center gap-1">
        <p className="text-sm font-medium">{t('onboarding.kbForm.step5.slots_block_title')}</p>
        <FieldHelp baseKey="onboarding.kbForm.step5.slots" />
      </div>
      <p className="text-xs text-muted-foreground">
        {t('onboarding.kbForm.step5.slots_block_hint')}
      </p>
      <SlotFieldsList
        slots={slots}
        onChange={(next) => {
          setSlots(next);
          setSaved(false);
        }}
        fieldErrors={fieldErrors}
        availableTypes={availableTypes}
      />
      <FieldError errors={fieldErrors} path="slot_definitions" />
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11"
          onClick={handleSave}
          disabled={saving}
          data-testid="objective-slots-save"
        >
          {t('onboarding.kbForm.step5.slots_save')}
        </Button>
        {saved && (
          <span className="text-xs text-muted-foreground" role="status">
            {t('onboarding.kbForm.step5.slots_saved')}
          </span>
        )}
      </div>
    </div>
  );
}

/** Family 2 — empty link library gets an inline first-link prompt; otherwise
 * the existing links are listed with an edit shortcut into the LinksEditor. */
function LinkSubConfigBlock() {
  const { t } = useTranslation();
  const { kbProfile, updateKbProfile } = useOnboardingState();
  const links = kbProfile?.link_library ?? [];
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  async function handleAdd() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      await updateKbProfile({ link_library: [{ label: label.trim(), url: url.trim() }] });
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  if (links.length === 0) {
    return (
      <div data-testid="objective-link-prompt" className="space-y-3">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium">{t('onboarding.kbForm.step5.links_block_title')}</p>
          <FieldHelp baseKey="onboarding.kbForm.step5.links" />
        </div>
        <p className="text-xs text-muted-foreground">
          {t('onboarding.kbForm.step5.links_empty_hint')}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="objective-link-label">
              {t('onboarding.kbForm.step5.link_label_field')}
            </Label>
            <Input
              id="objective-link-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('onboarding.kbForm.step5.link_label_placeholder')}
              maxLength={CAPS.link_label}
              className="min-h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="objective-link-url">
              {t('onboarding.kbForm.step5.link_url_field')}
            </Label>
            <Input
              id="objective-link-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('onboarding.kbForm.step5.link_url_placeholder')}
              maxLength={CAPS.link_url}
              className="min-h-11"
            />
          </div>
        </div>
        <FieldError errors={fieldErrors} path="link_library.0.url" />
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11"
          onClick={handleAdd}
          disabled={saving || !label.trim() || !url.trim()}
          data-testid="objective-link-add"
        >
          <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
          {t('onboarding.kbForm.step5.links_add')}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="objective-links-list" className="space-y-3">
      <div className="flex items-center gap-1">
        <p className="text-sm font-medium">{t('onboarding.kbForm.step5.links_block_title')}</p>
        <FieldHelp baseKey="onboarding.kbForm.step5.links" />
      </div>
      <ul className="space-y-1">
        {links.map((link) => (
          <li key={link.label} className="text-sm flex items-start gap-2 min-w-0">
            <LinkIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="min-w-0 flex-1 flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
              <span className="font-medium min-w-0 truncate sm:max-w-[60%]">{link.label}</span>
              <span className="text-xs text-muted-foreground truncate min-w-0">{link.url}</span>
            </div>
          </li>
        ))}
      </ul>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11"
        onClick={() => setEditorOpen(true)}
        data-testid="objective-links-edit"
      >
        <Pencil className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
        {t('onboarding.kbForm.step5.links_edit')}
      </Button>
      {editorOpen && (
        <LinksEditor open={editorOpen} onOpenChange={setEditorOpen} initialLinks={links} />
      )}
    </div>
  );
}
